import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import type { AdvancesFilters, AdvancesListResponse, AdvanceDetail, BusinessTripDetail, ReviewApplicationDto, CreateBusinessTripDto, SettleAdvanceDto, SettleBusinessTripDto, IssueAdvanceDto, RecommendedAllocationResponse } from "../types";

export const advancesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdvances: builder.query<AdvancesListResponse, Partial<AdvancesFilters>>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters.type) params.append("type", filters.type);
        if (filters.kind) params.append("kind", filters.kind);
        if (filters.status) params.append("status", filters.status);
        if (filters.dateFrom) params.append("date_from", filters.dateFrom);
        if (filters.dateTo) params.append("date_to", filters.dateTo);
        if (filters.search) params.append("search", filters.search);
        const qs = params.toString();
        return {
          url: qs ? `/api/accounting/advances/?${qs}` : `/api/accounting/advances/`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "Advances" as const, id: "LIST" }],
    }),
    getAdvanceDetail: builder.query<AdvanceDetail, number>({
      query: (id) => ({
        url: `/api/accounting/advances/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "Advances" as const, id }],
    }),
    getBusinessTripDetail: builder.query<BusinessTripDetail, number>({
      query: (id) => ({
        url: `/api/accounting/business-trips/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "BusinessTrips" as const, id }],
    }),
    reviewAdvance: builder.mutation<AdvanceDetail, ReviewApplicationDto>({
      query: ({ id, action, note }) => ({
        url: `/api/accounting/advances/${id}/review-application/`,
        method: "POST",
        body: { action, note },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Advances" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
    reviewBusinessTrip: builder.mutation<BusinessTripDetail, ReviewApplicationDto>({
      query: ({ id, action, note }) => ({
        url: `/api/accounting/business-trips/${id}/review-application/`,
        method: "POST",
        body: { action, note },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "BusinessTrips" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
    issueAdvance: builder.mutation<AdvanceDetail, IssueAdvanceDto>({
      query: ({ id, from_cash_desk, project_allocations }) => {
        const body: Partial<Pick<IssueAdvanceDto, "from_cash_desk" | "project_allocations">> = {};
        if (from_cash_desk !== undefined) body.from_cash_desk = from_cash_desk;
        if (project_allocations && project_allocations.length > 0) {
          body.project_allocations = project_allocations;
        }
        return {
          url: `/api/accounting/advances/${id}/issue/`,
          method: "POST",
          body,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Advances" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "Budget",
        "SmetaData",
        "PendingActions",
      ],
    }),
    getRecommendedAllocation: builder.query<RecommendedAllocationResponse, number>({
      query: (workerId) => ({
        url: `/api/accounting/advances/recommended-allocation/${workerId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
    issueBusinessTrip: builder.mutation<BusinessTripDetail, number>({
      query: (id) => ({
        url: `/api/accounting/business-trips/${id}/issue/`,
        method: "POST",
        body: {},
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "BusinessTrips" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "Budget",
        "SmetaData",
        "PendingActions",
      ],
    }),
    downloadAdvanceT3: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/accounting/advances/${id}/t3-download/`,
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),
    downloadBusinessTripT3: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/accounting/business-trips/${id}/t3-download/`,
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),
    createBusinessTrip: builder.mutation<BusinessTripDetail, CreateBusinessTripDto>({
      query: (body) => ({
        url: "/api/accounting/business-trips/create/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "BusinessTrips" as const, id: "LIST" },
        { type: "Advances" as const, id: "LIST" },
        "TeamLeaveSchedule",
        "LeaveApplications",
        "PendingActions",
      ],
    }),
    settleAdvance: builder.mutation<AdvanceDetail, SettleAdvanceDto>({
      query: ({ id, lines }) => ({
        url: `/api/accounting/advances/${id}/settle/`,
        method: "POST",
        body: { lines },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Advances" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "Budget",
        "SmetaData",
        "PendingActions",
      ],
    }),
    settleBusinessTrip: builder.mutation<BusinessTripDetail, SettleBusinessTripDto>({
      query: ({ id, days, transport, lodging }) => ({
        url: `/api/accounting/business-trips/${id}/settle/`,
        method: "POST",
        body: { days, transport, lodging },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "BusinessTrips" as const, id },
        { type: "Advances" as const, id: "LIST" },
        "Budget",
        "SmetaData",
        "PendingActions",
      ],
    }),
    previewBusinessTripOrder: builder.mutation<FileResponse, number>({
      query: (tripId) => ({
        url: `/api/hr/business-trip-orders/preview/${tripId}/`,
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),
    signBusinessTripOrder: builder.mutation<BusinessTripDetail, { tripId: number; cms_signature: string }>({
      query: ({ tripId, cms_signature }) => ({
        url: `/api/hr/business-trip-orders/${tripId}/sign/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { tripId }) => [
        { type: "BusinessTrips" as const, id: tripId },
        { type: "BusinessTrips" as const, id: "LIST" },
        { type: "Advances" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted({ tripId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                item.action_required === "sign_document" &&
                "source_type" in item.details &&
                (item.details as SignDocumentDetails).source_type === "business_trip_order" &&
                item.id === tripId,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetAdvancesQuery,
  useGetAdvanceDetailQuery,
  useGetBusinessTripDetailQuery,
  useReviewAdvanceMutation,
  useReviewBusinessTripMutation,
  useIssueAdvanceMutation,
  useGetRecommendedAllocationQuery,
  useIssueBusinessTripMutation,
  useDownloadAdvanceT3Mutation,
  useDownloadBusinessTripT3Mutation,
  useCreateBusinessTripMutation,
  useSettleAdvanceMutation,
  useSettleBusinessTripMutation,
  usePreviewBusinessTripOrderMutation,
  useSignBusinessTripOrderMutation,
} = advancesApi;
